/**
 * 碎片组管理器
 * 管理所有碎片组的创建、合并、查询
 */
import { Vector2 } from '../types';
import { PuzzlePiece } from './PuzzlePiece';
import { PieceGroup } from './PieceGroup';
import { HexMath } from '../core/HexMath';

export interface GroupManagerEvents {
    onGroupCreated?: (group: PieceGroup) => void;
    onGroupMerged?: (survivingGroup: PieceGroup, mergedGroup: PieceGroup) => void;
    onGroupPlaced?: (group: PieceGroup) => void;
}

export class GroupManager {
    private groups: Map<string, PieceGroup> = new Map();
    private pieceToGroup: Map<string, string> = new Map();  // pieceId -> groupId
    private events: GroupManagerEvents = {};
    private groupIdCounter: number = 0;

    constructor() { }

    setEvents(events: GroupManagerEvents): void {
        this.events = { ...this.events, ...events };
    }

    private generateGroupId(): string {
        return `group_${Date.now()}_${this.groupIdCounter++}`;
    }

    getGroupByPiece(piece: PuzzlePiece): PieceGroup | null {
        const groupId = this.pieceToGroup.get(piece.id);
        if (!groupId) return null;
        return this.groups.get(groupId) || null;
    }

    getGroupByPieceId(pieceId: string): PieceGroup | null {
        const groupId = this.pieceToGroup.get(pieceId);
        if (!groupId) return null;
        return this.groups.get(groupId) || null;
    }

    getGroup(groupId: string): PieceGroup | null {
        return this.groups.get(groupId) || null;
    }

    getAllGroups(): PieceGroup[] {
        return Array.from(this.groups.values());
    }

    createGroup(piece1: PuzzlePiece, piece2: PuzzlePiece): PieceGroup {
        const group = new PieceGroup(this.generateGroupId());
        group.addPiece(piece1, true);
        group.addPiece(piece2, false);
        this.groups.set(group.id, group);
        this.pieceToGroup.set(piece1.id, group.id);
        this.pieceToGroup.set(piece2.id, group.id);
        this.events.onGroupCreated?.(group);
        return group;
    }

    addPieceToGroup(piece: PuzzlePiece, group: PieceGroup): void {
        group.addPiece(piece, false);
        this.pieceToGroup.set(piece.id, group.id);
    }

    mergeGroups(group1: PieceGroup, group2: PieceGroup): PieceGroup {
        if (group1.id === group2.id) return group1;
        for (const pieceId of group2.getPieceIds()) {
            this.pieceToGroup.set(pieceId, group1.id);
        }
        group1.mergeGroup(group2);
        this.groups.delete(group2.id);
        this.events.onGroupMerged?.(group1, group2);
        return group1;
    }

    handleSnap(piece1: PuzzlePiece, piece2: PuzzlePiece): PieceGroup {
        const group1 = this.getGroupByPiece(piece1);
        const group2 = this.getGroupByPiece(piece2);

        if (!group1 && !group2) return this.createGroup(piece1, piece2);
        if (group1 && !group2) {
            this.addPieceToGroup(piece2, group1);
            return group1;
        }
        if (!group1 && group2) {
            this.addPieceToGroup(piece1, group2);
            return group2;
        }
        if (group1!.pieceCount >= group2!.pieceCount) {
            return this.mergeGroups(group1!, group2!);
        } else {
            return this.mergeGroups(group2!, group1!);
        }
    }

    movePiece(piece: PuzzlePiece, delta: Vector2): void {
        const group = this.getGroupByPiece(piece);
        if (group) group.move(delta);
        else piece.move(delta);
    }

    rotatePiece(piece: PuzzlePiece, clockwise: boolean): void {
        const group = this.getGroupByPiece(piece);
        if (group) group.rotate(clockwise);
        else piece.rotate(clockwise);
    }

    getGroupPieces(piece: PuzzlePiece): PuzzlePiece[] {
        const group = this.getGroupByPiece(piece);
        return group ? group.getPieces() : [piece];
    }

    canPlace(piece: PuzzlePiece, snapDistance: number): boolean {
        const group = this.getGroupByPiece(piece);
        if (group) return group.canPlace(snapDistance);
        if (piece.rotation !== 0) return false;
        return HexMath.distancePoints(piece.position, piece.correctPosition) <= snapDistance;
    }

    place(piece: PuzzlePiece): void {
        const group = this.getGroupByPiece(piece);
        if (group) {
            group.place();
            this.events.onGroupPlaced?.(group);
        } else {
            piece.setPosition(piece.correctPosition);
            piece.setRotation(0);
            piece.place();
        }
    }

    unplace(piece: PuzzlePiece): void {
        const group = this.getGroupByPiece(piece);
        if (group) group.unplace();
        else piece.unplace();
    }

    getSnapToCorrectDelta(piece: PuzzlePiece, snapDistance: number): Vector2 | null {
        const group = this.getGroupByPiece(piece);
        if (group) return group.getSnapToCorrectDelta(snapDistance);
        if (piece.rotation !== 0) return null;
        const delta = HexMath.sub(piece.correctPosition, piece.position);
        if (HexMath.vectorLength(delta) <= snapDistance) return delta;
        return null;
    }

    clear(): void {
        this.groups.clear();
        this.pieceToGroup.clear();
        this.groupIdCounter = 0;
    }

    exportState(): any[] {
        return this.getAllGroups().map(g => g.exportState());
    }

    importState(states: any[], getPiece: (id: string) => PuzzlePiece | undefined): void {
        this.clear();
        for (const state of states) {
            const group = new PieceGroup(state.id);
            let isFirst = true;
            for (const pieceId of state.pieceIds) {
                const piece = getPiece(pieceId);
                if (piece) {
                    group.addPiece(piece, isFirst);
                    this.pieceToGroup.set(pieceId, group.id);
                    isFirst = false;
                }
            }
            if (group.pieceCount > 0) this.groups.set(group.id, group);
        }
    }
}